import { OverlayToaster, Position, Toast2 } from "@blueprintjs/core"
import { useQueryClient } from "@tanstack/react-query"
import { createContext, useCallback, useEffect, useMemo } from "react"

import { IUser } from "../api/index"
import { Loader } from "./components"
import { useAuth, useQueryGroups, useQueryIdeasStats, useQueyUsers } from "./hooks"
import { useSapiAuthorizationUpdated, useNotificationEvent } from "../websocket/hooks/useEvents"
import { toaster } from "./utils/createToaster"

export interface IScientGlobalContext {
  groups: {
    id: number
    name: string
  }[]
  users: IUser[]
  ideasStats:
    | {
        unseen_ideas_count: number
      }
    | undefined
  updateUnseenIdeasCount?: (number: number) => void
}

/**
 * Scient Global context
 */
export const ScientGlobalContext = createContext<IScientGlobalContext>({
  groups: [],
  users: [],
  ideasStats: undefined,
  updateUnseenIdeasCount: undefined,
})

/**
 * Provides access to children to global data
 * Includes:
 * - profile
 * - users
 * - groups
 * - ideas stats
 */
const ScientGlobalStateProvider = ({ children }: { children: JSX.Element }) => {
  const { profile } = useAuth()
  const { data: groupsData, isLoading: isLoadingGroups, error: errorGroups } = useQueryGroups()
  const { data: usersData, isLoading: isLoadingUsers, error: errorUsers } = useQueyUsers()
  const {
    data: ideasStatsData,
    isLoading: isLoadingIdeasStats,
    error: errorIdeasStats,
  } = useQueryIdeasStats()

  const queryClient = useQueryClient()

  /**
   * Setup intercom when profile is loaded
   */
  useEffect(() => {
    if (profile) {
      if (process.env.REACT_APP_INTERCOM === "true") {
        // @ts-ignore
        window.Intercom("boot", {
          api_base: "https://api-iam.intercom.io",
          app_id: "lk32h9na",
          name: profile?.username, // Full name
          email: profile?.email, // the email for your user
          created_at: null, // Signup date as a Unix timestamp
        })
      }
    }
  }, [profile])

  /**
   * Updates ideas stat in the cache
   */
  const updateUnseenIdeasCount = useCallback(
    (number: number) => {
      queryClient.setQueryData<{ unseen_ideas_count: number }>(["ideasStats"], ideasStatsData => {
        if (ideasStatsData) {
          const newCount = ideasStatsData.unseen_ideas_count + number
          return {
            ...ideasStatsData,
            unseen_ideas_count: newCount,
          }
        }
      })
    },
    [queryClient],
  )

  /**
   * Memoized state for context change
   * and children prop propagation
   */
  const state = useMemo(
    () => ({
      profile,
      groups: groupsData || [],
      users: usersData || [],
      ideasStats: ideasStatsData,
      updateUnseenIdeasCount,
    }),
    [profile, groupsData, usersData, ideasStatsData, updateUnseenIdeasCount],
  )

  /**
   * Handles SAPI authorization events from websocket
   */
  useSapiAuthorizationUpdated(event => {
    switch (event.status) {
      case "AuthorizationSuccess":
        toaster.show({
          message: "Successfully connected to Bloomberg - SAPI",
          intent: "success",
        })
        break
      case "AuthorizationFailure":
        toaster.show({
          message:
            "Failed to connect to Bloomberg - SAPI. You either don't have access, your token has expired or you are connected on another device.",
          intent: "danger",
          timeout: 10000,
        })
        break
      case "AuthorizationRevoked":
        toaster.show({
          message: "Connection to Bloomberg - SAPI has been revoked by BSSO. Please reconnect.",
          intent: "danger",
          timeout: 10000,
        })
        break
    }
    /**
     * Invalidate profile and trading data queries to force a re-fetch
     * and display the new Bloomberg SAPI connection status
     */
    queryClient.invalidateQueries({ queryKey: ["profile"] })
    queryClient.invalidateQueries({ queryKey: ["tradingdatascient"] })
  })

  /**
   * Handles notification events from websocket
   */
  useNotificationEvent(event => {
    toaster.show({
      message: event.message,
      intent: event.intent,
      timeout: event.timeout || 5000,
    })
  })

  /**
   * If one of those request fails, app cannot boot
   * and we invite the user to refresh the page
   */
  return (
    <>
      {(errorGroups || errorUsers || errorIdeasStats) && (
        <OverlayToaster position={Position.TOP}>
          <Toast2
            message="An error occurred initializing application, please refresh the page"
            intent="danger"
          />
        </OverlayToaster>
      )}
      {isLoadingGroups || errorGroups ? (
        <Loader title="Loading groups..." />
      ) : isLoadingUsers || errorUsers ? (
        <Loader title="Loading users..." />
      ) : isLoadingIdeasStats || errorIdeasStats ? (
        <Loader title="Loading stats..." />
      ) : (
        <ScientGlobalContext.Provider value={state}>{children}</ScientGlobalContext.Provider>
      )}
    </>
  )
}

export default ScientGlobalStateProvider
